import React, { useState, useEffect, useRef } from 'react';
import './App.css';

function BtsProjects({ photos, movies, projectId }) {
  const [activeTab, setActiveTab] = useState('videos');
  const containerRef = useRef(null);
  const videoRefs = useRef([]);
  const observerRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);


  // Set up Intersection Observer for scroll-based video playback
  useEffect(() => {
    if (activeTab !== 'videos') return;

    // Clean up previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Create new observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = Number(entry.target.dataset.index);
          if (entry.isIntersecting) {
            setCurrentIndex(index);
            // Play current video
            videoRefs.current.forEach((vid, i) => {
              if (vid) {
                if (i === index) {
                  // For regular videos, play them
                  if (!movies[i].isYouTube) {
                    vid.play().catch((error) => {
                      console.log("Video play failed:", error);
                    });
                  }
                } else {
                  // Pause other videos
                  if (!movies[i].isYouTube) {
                    vid.pause();
                    vid.currentTime = 0;
                  }
                }
              }
            });
          }
        });
      },
      { threshold: 0.6 }
    );

    observerRef.current = observer;

    // Observe all video items
    setTimeout(() => {
      const items = document.querySelectorAll(".bts-reel-item");
      items.forEach((item) => observer.observe(item));
    }, 100);

    // Handle hash navigation
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash === '#photos') {
        setActiveTab('photos');
        // Scroll to photos section
        setTimeout(() => {
          const element = document.querySelector('.bts-section');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      } else if (hash.startsWith('#video-')) {
        // The hash #video-{id} now represents the PROJECT ID, not the video index.
        // We just want to ensure we are on the videos tab.
        setActiveTab('videos');

        // Optional: Scroll to the top of the video list if needed, or just the section
        setTimeout(() => {
          const element = document.querySelector('.bts-video-wrapper');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    };

    // Check hash on initial load
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [activeTab, movies]);



  return (
    <div className="bts-projects-page">
      {/* Main Content - "BTS Projects" */}
      <main className="projects-main">
        <section className="projects-hero">
          <h1 className="projects-heading">BTS GALLERY</h1>

          {/* Tab Navigation */}
          <div className="bts-tabs">
            <button
              className={`tab-button ${activeTab === 'photos' ? 'active' : ''}`}
              onClick={() => setActiveTab('photos')}
              style={{
                animation: 'fadeIn 0.5s ease-out 0.1s both',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                if (activeTab !== 'photos') {
                  e.target.style.transform = 'scale(1.05)';
                  e.target.style.boxShadow = '0 0 10px rgba(153, 51, 255, 0.5)';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'photos') {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = 'none';
                }
              }}
            >
              Photos
            </button>
            <button
              className={`tab-button ${activeTab === 'videos' ? 'active' : ''}`}
              onClick={() => setActiveTab('videos')}
              style={{
                animation: 'fadeIn 0.5s ease-out 0.2s both',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                if (activeTab !== 'videos') {
                  e.target.style.transform = 'scale(1.05)';
                  e.target.style.boxShadow = '0 0 10px rgba(153, 51, 255, 0.5)';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'videos') {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = 'none';
                }
              }}
            >
              Videos
            </button>
          </div>

          {/* Photo Section */}
          {activeTab === 'photos' && (
            <div className="bts-section">
              <div className="projects-grid" style={{ animation: 'fadeIn 0.8s ease-out 0.3s both' }}>
                {photos.map((photo, index) => (
                  <div className="project-poster" key={photo.id} style={{ animation: `fadeInUp 0.5s ease-out ${0.1 * index}s both` }}>
                    <img src={photo.imageUrl} alt={photo.title} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Video Section with Scroll-based Layout (from BtsVideo.js) */}
          {activeTab === 'videos' && (
            <div className="bts-video-wrapper">


              {/* Main Section */}
              <div className="bts-main">
                {/* Left Info (sticky) */}
                <div className="bts-info">
                  {movies.length > 0 && (
                    <>
                      <h2 className="movie-name animated-content" style={{ animation: 'fadeInUp 0.6s ease-out 0.1s both' }}>{movies[currentIndex].title}</h2>
                      <p className="movie-description animated-content" style={{ animation: 'fadeInUp 0.6s ease-out 0.2s both', color: '#FFFFFF' }}>
                        {movies[currentIndex].description}
                      </p>
                    </>
                  )}
                </div>

                {/* Right Scroll Reels with Film Strip Frame */}
                <div className="bts-reel-frame">
                  <div className="bts-reel-scroll">
                    {movies.map((movie, index) => (
                      <div
                        key={movie.id}
                        className="bts-reel-item"
                        data-index={index}
                      >
                        {/* Film Strip Container for each video */}
                        <div className="film-frame-container">
                          <div className="film-strip-overlay"></div>
                          <div className="film-frame-content">
                            <div className="video-block-container">
                              {movie.isYouTube ? (
                                <iframe
                                  src={movie.videoUrl}
                                  title={movie.title}
                                  frameBorder="0"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                  style={{ width: '100%', height: '100%' }}
                                ></iframe>
                              ) : (
                                <video
                                  ref={(el) => (videoRefs.current[index] = el)}
                                  playsInline
                                  loop
                                  muted={false} // Ensure it's not strictly muted if controls are there, though autoplay might require mute initially. 
                                  // Actually, removing muted allows sound if the user un-mutes. Observer play() might need mute for autoplay?
                                  // Let's just add controls. The user can unmute.
                                  controls
                                  preload="metadata"
                                >
                                  <source src={movie.videoUrl} type="video/mp4" />
                                  Your browser does not support the video tag.
                                </video>
                              )}
                            </div>
                          </div>
                        </div>

                       
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>
      </main >
    </div >
  );
}

export default BtsProjects;